import type { SVGProps } from "react";

export type IconName = "eye" | "text" | "euro" | "chat" | "key" | "user" | "server" | "receipt";

type P = SVGProps<SVGSVGElement>;

const stroke = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

export const ArrowRight = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

export const ArrowUpRight = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="M7 17 17 7" />
    <path d="M8 7h9v9" />
  </svg>
);

export const Check = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </svg>
);

export const Cross = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="M6 6l12 12" />
    <path d="M18 6 6 18" />
  </svg>
);

export const Plus = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

export const Menu = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </svg>
);

export const Phone = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
  </svg>
);

export const Mail = (p: P) => (
  <svg {...stroke} {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

export const Play = (p: P) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...p}>
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
);

export const Star = (p: P) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...p}>
    <path d="M12 2.8l2.8 6 6.5.7-4.8 4.5 1.3 6.5L12 17.3l-5.8 3.2 1.3-6.5L2.7 9.5l6.5-.7z" />
  </svg>
);

/** Sprechblase mit Hörer: generisches Messenger-Symbol */
export const Chat = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="M12 3.5a8.5 8.5 0 0 0-7.3 12.9L3.5 20.5l4.3-1.1A8.5 8.5 0 1 0 12 3.5z" />
    <path d="M9.3 9.2c.3-.6 1-.6 1.3 0l.5 1.1-.8 1a4.6 4.6 0 0 0 2.4 2.4l1-.8 1.1.5c.6.3.6 1 0 1.3-.9.6-2 .5-3.2-.3a7 7 0 0 1-2.6-2.6c-.8-1.2-.9-2.3-.3-3.2z" />
  </svg>
);

export const Shield = (p: P) => (
  <svg {...stroke} {...p}>
    <path d="M12 3 5 6v5c0 4.5 3 8.5 7 10 4-1.5 7-5.5 7-10V6z" />
    <path d="m9 12 2 2 4-4.5" />
  </svg>
);

export const Image = (p: P) => (
  <svg {...stroke} {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <circle cx="9" cy="9.5" r="1.6" />
    <path d="m21 16-5-5-8 8" />
  </svg>
);

const benefitPaths: Record<IconName, React.ReactNode> = {
  eye: (
    <>
      <path d="M2.5 12S6 6 12 6s9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  text: (
    <>
      <path d="M4 6h16" />
      <path d="M4 12h11" />
      <path d="M4 18h7" />
    </>
  ),
  euro: (
    <>
      <path d="M17.5 6.8A6.5 6.5 0 0 0 7.8 9.5" />
      <path d="M17.5 17.2a6.5 6.5 0 0 1-9.7-2.7" />
      <path d="M4 10.5h9" />
      <path d="M4 13.5h8" />
    </>
  ),
  chat: (
    <>
      <path d="M4 5h16v11H9.5L4.5 20z" />
      <path d="M8 9h8" />
      <path d="M8 12.5h5" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="3.5" />
      <path d="m10.5 12.5 8-8" />
      <path d="m16 7 2.5 2.5" />
      <path d="m13.5 9.5 2 2" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3.8" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  server: (
    <>
      <rect x="3.5" y="4" width="17" height="6.5" rx="2" />
      <rect x="3.5" y="13.5" width="17" height="6.5" rx="2" />
      <path d="M7.5 7.25h.01" />
      <path d="M7.5 16.75h.01" />
      <path d="M11.5 7.25h5" />
      <path d="M11.5 16.75h5" />
    </>
  ),
  receipt: (
    <>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6" />
      <path d="M9 12h6" />
      <path d="M9 16h3" />
    </>
  ),
};

export function BenefitIcon({ name, ...p }: P & { name: IconName }) {
  return (
    <svg {...stroke} {...p}>
      {benefitPaths[name]}
    </svg>
  );
}
